/**
 * S05b — THE FILMS (Odyssey Map)
 *
 * Hero: HF#8 god's-eye overhead shot of the odyssey dining table
 * (maps, wine, truffles, coffee beans, passport, knife). Full-bleed
 * background. On top: a scroll-dragged gold ink route draws between
 * Queens → Alba → Huila waypoint pins. Coordinate ticker updates.
 * The table IS the map.
 *
 * Restored from pre-narrative-refactor (commit 568a262^) and re-inserted
 * after the three Act sections to break up the text-heavy spine with
 * the geography beat.
 */

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";
import { odysseyRoute, interpolateRoute, formatCoord } from "@/lib/coords";
import tableSrc from "@/assets/higgsfield/hf-08-odyssey-table.webp";

gsap.registerPlugin(ScrollTrigger);

// Waypoint positions on top of the HF#8 table image (percentages of the
// SVG coordinate space — which preserveAspectRatio="none" maps directly
// onto the container, so xPct and yPct act like CSS percentages).
//
// Desktop = the diagonal arc across the table that mirrors the
// god's-eye photography (Queens upper-right → Alba center → Huila
// lower-left). On mobile portrait, that arc gets crushed into the
// title band, so we switch to a vertical stack down the center.
type Pin = { id: string; xPct: number; yPct: number };

const PINS_DESKTOP: Pin[] = [
  { id: "queens", xPct: 78, yPct: 40 },
  { id: "alba", xPct: 50, yPct: 45 },
  { id: "huila", xPct: 30, yPct: 65 },
];

const PINS_MOBILE: Pin[] = [
  { id: "queens", xPct: 50, yPct: 11 },
  { id: "alba", xPct: 50, yPct: 23 },
  { id: "huila", xPct: 50, yPct: 35 },
];

function buildRoutePath(pins: Pin[]): string {
  // SVG coords: xPct * 10 (out of 1000), yPct * 6 (out of 600).
  // Quadratic + smooth-quadratic curve through 3 waypoints with a
  // control point that sits 12% above the first waypoint, giving the
  // route a natural arc/sway between pins.
  return `M ${pins[0].xPct * 10} ${pins[0].yPct * 6} Q ${(pins[0].xPct + pins[1].xPct) * 5} ${Math.max(0, pins[0].yPct - 12) * 6}, ${pins[1].xPct * 10} ${pins[1].yPct * 6} T ${pins[2].xPct * 10} ${pins[2].yPct * 6}`;
}

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [breakpoint]);
  return isMobile;
}

export function S05b_TheFilms() {
  const copy = sectionCopy.s05b;
  const isMobile = useIsMobile();
  const PINS = isMobile ? PINS_MOBILE : PINS_DESKTOP;
  const ROUTE_PATH = buildRoutePath(PINS);
  const sectionRef = useRef<HTMLElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    const path = pathRef.current;
    if (!el || !path) return;

    // Recompute on mount and whenever the path d-attribute changes
    // (i.e. when the layout swaps between mobile/desktop).
    const length = path.getTotalLength();
    path.style.strokeDasharray = String(length);
    path.style.strokeDashoffset = String(length * (1 - progress));

    // On mobile the pin column lives in the upper third of the section,
    // so the route should fully draw well before the section bottom hits
    // the viewport top. On desktop the arc spans most of the section so
    // we keep the original wide trigger window.
    const triggerEnd = isMobile ? "top+=55% center" : "bottom 30%";

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: el,
        start: "top 70%",
        end: triggerEnd,
        scrub: 1,
        onUpdate: (self) => {
          setProgress(self.progress);
          path.style.strokeDashoffset = String(length * (1 - self.progress));
        },
      });
    }, el);
    return () => ctx.revert();
    // Re-run if the route path or breakpoint changes (responsive
    // layout swap). Intentionally omit `progress` — we read its
    // current value once for the initial dashoffset, but a new
    // ScrollTrigger always overwrites it on the first scroll tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ROUTE_PATH, isMobile]);

  const coords = interpolateRoute(progress);

  return (
    <section
      ref={sectionRef}
      id="s05b"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: isMobile ? "8vh var(--space-md)" : "10vh var(--space-lg)",
        minHeight: isMobile ? "100vh" : "110vh",
      }}
    >
      {/* Full-bleed table hero */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${tableSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.72) contrast(1.08) saturate(0.92)",
          zIndex: 0,
        }}
      />
      {/* Vignette crush */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.88) 100%)",
          zIndex: 1,
        }}
      />

      {/* Gold route — SVG path only. Uses preserveAspectRatio="none"
          so it stretches to fill the container; the route is just a
          line so non-uniform stretching is fine. */}
      <svg
        viewBox="0 0 1000 600"
        preserveAspectRatio="none"
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        <path
          ref={pathRef}
          d={ROUTE_PATH}
          fill="none"
          stroke="var(--gold)"
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          filter="drop-shadow(0 4px 12px rgba(242,193,78,0.6))"
        />
      </svg>

      {/* Pins + labels — rendered as HTML so circles stay round and
          text stays at real px font sizes regardless of the section's
          aspect ratio. Each pin is positioned as a percentage of the
          section box, matching the SVG route's coordinate space. */}
      {PINS.map((pin, i) => {
        const frac = i / (PINS.length - 1);
        const visible = progress >= frac - 0.05;
        const wp = odysseyRoute[i];
        return (
          <div
            key={pin.id}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: `${pin.xPct}%`,
              top: `${pin.yPct}%`,
              transform: "translate(-50%, -50%)",
              opacity: visible ? 1 : 0,
              transition: "opacity 500ms cubic-bezier(0.87,0,0.13,1)",
              zIndex: 3,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {/* Label above */}
            <div
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: "13px",
                color: "var(--gold)",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                textShadow: "0 2px 6px rgba(0,0,0,0.9)",
                whiteSpace: "nowrap",
                lineHeight: 1,
              }}
            >
              {wp.name}
            </div>

            {/* Pin dot — outer ring + inner gold core */}
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "var(--ink)",
                border: "2px solid var(--gold)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 12px rgba(242,193,78,0.5)",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: "var(--gold)",
                }}
              />
            </div>

            {/* Sub coordinate label */}
            <div
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: "10px",
                color: "var(--bone)",
                letterSpacing: "0.18em",
                opacity: 0.8,
                textShadow: "0 2px 6px rgba(0,0,0,0.9)",
                whiteSpace: "nowrap",
                lineHeight: 1,
              }}
            >
              {wp.sub}
            </div>
          </div>
        );
      })}

      <MonumentType word={copy.monument} theme="ink" />

      {/* Content block — on desktop sits vertically centered (margin auto),
          on mobile drops just below the pin column. Pins occupy ~11–35%
          of section height with the sub-label trailing to ~38%; the
          title content sits at ~40% so it pulls the eye into the
          center of the viewport while still leaving room above the
          coordinate ticker (which we drop to bottom 5vh on mobile so
          the long italic subtitle doesn't bleed into it). */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          padding: isMobile ? "var(--space-sm)" : "var(--space-lg)",
          marginTop: isMobile ? "32vh" : "auto",
          marginBottom: isMobile ? "var(--space-md)" : "auto",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--gold)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta} — THE ODYSSEY
        </div>
        <h2
          className="type-hero-lg"
          style={{
            color: "var(--bone)",
            margin: 0,
          }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.25rem, 1.6vw, 1.75rem)",
            }}
          >
            {copy.subhero}
          </p>
        )}
      </div>

      {/* Coordinate ticker — pinned to the section bottom corner. On
          mobile we slide it nearly flush with the bottom edge so the
          long italic subtitle has clean room above it. */}
      <div
        className="type-meta-sm"
        style={{
          position: "absolute",
          bottom: isMobile ? "4vh" : "14vh",
          right: isMobile ? "var(--space-md)" : "var(--space-lg)",
          background: "var(--ink)",
          color: "var(--gold)",
          padding: "var(--space-xs) var(--space-sm)",
          fontVariantNumeric: "tabular-nums",
          border: "1px solid var(--gold)",
          zIndex: 4,
          boxShadow: "0 0 30px rgba(242,193,78,0.3)",
        }}
      >
        {formatCoord(coords.lat, coords.lng)}
      </div>
    </section>
  );
}
